import { errorSummary } from "./format.ts";
import { radioEventParams, trackEvent } from "./analytics.ts";
import { refusedSettingIssues } from "../runtime-errors.ts";
import { normalizeSettingValue } from "./setting-values.ts";
import { requireRuntimeApi } from "./state.ts";
import type { UiContext } from "../types/ui-context.js";
import type { SettingValueMeta } from "./setting-fields.ts";
import type { SettingIssue, SettingLeafNode, SettingNode } from "../runtime-rpc.ts";
import type { RadioSessionHandle } from "./state.ts";

/**
 * One value of one setting, flattened out of the settings tree: where it is
 * (the setting's path and the value's index in it) and the tree's own value
 * object, which a merge writes current back into.
 */
/** The settings tree the panel shows, and whether there is one. */
interface SettingsState {
  supported: boolean;
  available: boolean;
  requiresImage: boolean;
  message: string;
  groups: SettingNode[];
}

interface FlatSettingField {
  path: unknown[];
  valueIndex: number;
  current: unknown;
  valueRef: SettingValueMeta;
}

// Radio-wide settings: the tabbed editor, its per-value validation, and the
// load/merge path from the Python runtime. Owns the settings tree and the
// invalid-value bookkeeping; other modules reach it through the returned API.
export function createSettingsPanel(ctx: UiContext) {
  const { dom, state, log, actions } = ctx;
  let settingsState: SettingsState = {
    supported: false,
    available: false,
    requiresImage: false,
    message: "",
    groups: [],
  };
  let activeTab = "";
  const invalidKeys = new Set<string>();
  const invalidMessages = new Map<string, string>();

  // A deep copy, through JSON: the tree is plain data the runtime produced.
  function cloneGroups(groups: readonly SettingNode[] | null | undefined): SettingNode[] {
    return JSON.parse(JSON.stringify(Array.isArray(groups) ? groups : []));
  }

  function settingKey(path: readonly unknown[] | null | undefined, valueIndex = 0): string {
    return `${(Array.isArray(path) ? path : []).join("/")}:${Number(valueIndex)}`;
  }

  function clearInvalid() {
    invalidKeys.clear();
    invalidMessages.clear();
  }

  function clearInvalidSetting(path: readonly unknown[], valueIndex = 0): void {
    const key = settingKey(path, valueIndex);
    invalidKeys.delete(key);
    invalidMessages.delete(key);
  }

  function radioHasSettings() {
    return Boolean(
      settingsState?.available &&
      Array.isArray(settingsState.groups) &&
      settingsState.groups.length > 0,
    );
  }

  function hasInvalidSettings() {
    return invalidKeys.size > 0;
  }

  function settingsUnavailableMessage() {
    return settingsState?.message || "This radio does not expose radio-wide settings.";
  }

  function updateViewButtons() {
    dom.viewSettingsEl.disabled = !radioHasSettings();
    dom.viewSettingsEl.title = radioHasSettings()
      ? "Edit radio-wide settings"
      : (settingsState?.message || "This radio does not expose radio-wide settings");
  }

  function updateSummary() {
    const count = invalidKeys.size;
    dom.settingsSummaryEl.hidden = !radioHasSettings();
    dom.settingsSummaryEl.classList.toggle("has-invalid", count > 0);
    if (!radioHasSettings()) {
      dom.settingsSummaryEl.textContent = "";
      return;
    }
    dom.settingsSummaryEl.textContent = count > 0
      ? `Radio settings have ${count} invalid value${count === 1 ? "" : "s"}. Fix the highlighted fields before upload.`
      : "Radio settings are ready to write. Immutable values are shown but disabled.";
    actions.updateSerialActionState();
  }

  function flattenSettingsFields(groups: readonly SettingNode[] | null | undefined): FlatSettingField[] {
    const out: FlatSettingField[] = [];
    function walk(node: SettingNode | null | undefined) {
      if (!node) {
        return;
      }
      if (node.kind === "setting") {
        const values = Array.isArray(node.values) ? node.values : [];
        values.forEach((value, valueIndex) => {
          out.push({
            path: node.path || [],
            valueIndex,
            current: value.current,
            valueRef: value,
          });
        });
        return;
      }
      (node.children || []).forEach(walk);
    }
    (groups || []).forEach(walk);
    return out;
  }

  function setSettingValue(settingNode: SettingLeafNode, valueIndex: number, rawValue: unknown): void {
    const valueMeta = settingNode?.values?.[valueIndex];
    if (!valueMeta) {
      return;
    }
    const result = normalizeSettingValue(valueMeta, rawValue, valueMeta.current);
    valueMeta.current = result.value;
    const key = settingKey(settingNode.path, valueIndex);
    if (result.error) {
      invalidKeys.add(key);
      invalidMessages.set(key, result.error);
    } else {
      clearInvalidSetting(settingNode.path, valueIndex);
    }
    updateSummary();
    syncTabs();
  }

  function findSettingsTabNode(tabId: string): SettingNode | null {
    return settingsState.groups.find((group) => group.id === tabId) || null;
  }

  function tabHasInvalidSettings(group: SettingNode | null): boolean {
    if (!group) {
      return false;
    }
    return flattenSettingsFields([group]).some((field) =>
      invalidKeys.has(settingKey(field.path, field.valueIndex)));
  }

  // Show a field's error, or clear it, on the field's own wrapper. The error
  // goes last, after any warning, both when the field is built and when an
  // edit later changes its verdict.
  function showFieldError(wrapper: HTMLElement, errorText: string): void {
    wrapper.classList.toggle("is-invalid", Boolean(errorText));
    let errorEl = wrapper.querySelector<HTMLElement>(".settings-field-error");
    if (!errorText) {
      errorEl?.remove();
      return;
    }
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.className = "settings-field-error";
      wrapper.appendChild(errorEl);
    }
    errorEl.textContent = errorText;
  }

  function renderSettingControl(settingNode: SettingLeafNode, valueMeta: SettingValueMeta, valueIndex: number): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "settings-field-control";
    const key = settingKey(settingNode.path, valueIndex);
    // Mutability is per value: a multi-value setting can mix locked and
    // editable values, and the write path skips only the locked ones.
    const immutable = valueMeta.mutable === false;
    wrapper.classList.toggle("is-immutable", immutable);

    // Store an edit and bring this field up to date with it in place: the
    // stored value (normalization can rewrite what was typed) and its error.
    // Rebuilding the panel instead would destroy the control the user is on,
    // dropping keyboard focus to the page and swallowing the Tab that was
    // moving on from it (issue #96).
    function commit(rawValue: unknown): void {
      setSettingValue(settingNode, valueIndex, rawValue);
      showValue();
      showFieldError(wrapper, invalidMessages.get(key) || "");
    }

    let control: HTMLInputElement | HTMLSelectElement;
    let showValue: () => void;
    if (valueMeta.type === "boolean") {
      const checkbox = document.createElement("input");
      control = checkbox;
      checkbox.type = "checkbox";
      showValue = () => {
        checkbox.checked = Boolean(valueMeta.current);
      };
      checkbox.disabled = immutable;
      checkbox.addEventListener("change", () => commit(checkbox.checked));
    } else if (valueMeta.type === "enum") {
      const select = document.createElement("select");
      control = select;
      const options = Array.isArray(valueMeta.options) ? valueMeta.options : [];
      options.forEach((option) => {
        const optionEl = document.createElement("option");
        optionEl.value = String(option);
        optionEl.textContent = String(option);
        select.appendChild(optionEl);
      });
      showValue = () => {
        select.value = String(valueMeta.current ?? "");
      };
      select.disabled = immutable;
      select.addEventListener("change", () => commit(select.value));
    } else {
      const input = document.createElement("input");
      control = input;
      input.type = valueMeta.type === "integer" || valueMeta.type === "float" ? "number" : "text";
      if (valueMeta.type === "integer" || valueMeta.type === "float") {
        if (Number.isFinite(valueMeta.min)) {
          input.min = String(valueMeta.min);
        }
        if (Number.isFinite(valueMeta.max)) {
          input.max = String(valueMeta.max);
        }
        if (Number.isFinite(valueMeta.step)) {
          input.step = String(valueMeta.step);
        } else if (valueMeta.type === "float") {
          input.step = "any";
        }
      }
      if (Number.isFinite(valueMeta.maxLength)) {
        input.maxLength = Number(valueMeta.maxLength);
      }
      showValue = () => {
        input.value = String(valueMeta.current ?? "");
      };
      input.readOnly = immutable;
      input.disabled = immutable;
      input.addEventListener("change", () => commit(input.value));
    }
    showValue();

    wrapper.appendChild(control);

    if (settingNode.warning) {
      const warningEl = document.createElement("div");
      warningEl.className = "settings-field-warning";
      warningEl.textContent = settingNode.warning;
      wrapper.appendChild(warningEl);
    }
    showFieldError(wrapper, invalidMessages.get(key) || "");
    return wrapper;
  }

  function renderSettingNode(parentEl: HTMLElement, node: SettingNode): void {
    if (node.kind === "group") {
      const section = document.createElement("section");
      section.className = node.path?.length > 1 ? "settings-subgroup" : "settings-group";
      const heading = document.createElement(node.path?.length > 1 ? "h4" : "h3");
      heading.textContent = node.label || node.id;
      section.appendChild(heading);
      (node.children || []).forEach((child) => renderSettingNode(section, child));
      parentEl.appendChild(section);
      return;
    }

    const fields = document.createElement("div");
    fields.className = "settings-fields";
    const values = Array.isArray(node.values) ? node.values : [];
    values.forEach((valueMeta, valueIndex) => {
      const labelEl = document.createElement("div");
      labelEl.className = "settings-field-label";
      const labelStrong = document.createElement("strong");
      labelStrong.textContent = values.length > 1 ? `${node.label} ${valueIndex + 1}` : node.label;
      labelEl.appendChild(labelStrong);
      if (node.doc) {
        const docEl = document.createElement("div");
        docEl.className = "settings-field-doc";
        docEl.textContent = node.doc;
        labelEl.appendChild(docEl);
      }
      if (node.volatile) {
        const volatileEl = document.createElement("div");
        volatileEl.className = "settings-field-doc";
        volatileEl.textContent = "Volatile setting";
        labelEl.appendChild(volatileEl);
      }
      fields.appendChild(labelEl);
      fields.appendChild(renderSettingControl(node, valueMeta, valueIndex));
    });
    parentEl.appendChild(fields);
  }

  function render() {
    updateSummary();
    updateViewButtons();
    if (!dom.settingsTabsEl || !dom.settingsContentEl || !dom.settingsEmptyEl) {
      return;
    }

    dom.settingsTabsEl.innerHTML = "";
    dom.settingsContentEl.innerHTML = "";
    dom.settingsEmptyEl.textContent = settingsUnavailableMessage();

    if (!radioHasSettings()) {
      dom.settingsEmptyEl.hidden = false;
      dom.settingsContentEl.hidden = true;
      return;
    }

    dom.settingsEmptyEl.hidden = true;
    dom.settingsContentEl.hidden = false;

    const activeGroup = findSettingsTabNode(activeTab) || settingsState.groups[0];
    if (!activeGroup) {
      dom.settingsEmptyEl.hidden = false;
      dom.settingsContentEl.hidden = true;
      return;
    }
    activeTab = activeGroup.id;

    settingsState.groups.forEach((group) => {
      const tabButton = document.createElement("button");
      tabButton.type = "button";
      tabButton.className = "settings-tab";
      tabButton.dataset.settingsTab = group.id;
      tabButton.textContent = group.label || group.id;
      tabButton.addEventListener("click", () => {
        // Group ids come from the driver's own settings tree, so this says
        // which parts of a radio's configuration people actually go looking
        // for. Only the group id travels, never a setting value.
        trackEvent("settings_tab_opened", {
          ...radioEventParams(state.selectedRadio),
          tab: String(group.id || ""),
        });
        openTab(group.id);
      });
      dom.settingsTabsEl.appendChild(tabButton);
    });
    syncTabs();
    renderSettingNode(dom.settingsContentEl, activeGroup);
  }

  // Mark, on the tab buttons already in the panel, which tab is open and
  // which hold invalid values.
  function syncTabs(): void {
    dom.settingsTabsEl.querySelectorAll<HTMLElement>(".settings-tab").forEach((tabButton) => {
      const groupId = tabButton.dataset.settingsTab || "";
      tabButton.classList.toggle("is-active", groupId === activeTab);
      tabButton.classList.toggle("has-invalid", tabHasInvalidSettings(findSettingsTabNode(groupId)));
    });
  }

  // Switch tabs without rebuilding the tab strip, so the tab button the user
  // pressed keeps keyboard focus (issue #96); only the open group's controls
  // are replaced. A group that is gone takes the full render(), which falls
  // back to the first one.
  function openTab(groupId: string): void {
    activeTab = groupId;
    const group = findSettingsTabNode(groupId);
    if (!group) {
      render();
      return;
    }
    syncTabs();
    dom.settingsContentEl.innerHTML = "";
    renderSettingNode(dom.settingsContentEl, group);
  }

  // Keep the active tab pointing at a group that still exists.
  function ensureActiveTab() {
    if (!activeTab || !settingsState.groups.some((group) => group.id === activeTab)) {
      activeTab = settingsState.groups[0]?.id || "";
    }
  }

  // The settings state for a session's radio, or the empty state for none. A
  // runtime failure becomes an "unavailable" state rather than a throw, so a
  // driver whose settings cannot be built still loads its channel schema.
  async function fetchForSession(session: RadioSessionHandle | null): Promise<SettingsState> {
    if (!session) {
      return {
        supported: false,
        available: false,
        requiresImage: false,
        message: "",
        groups: [],
      };
    }
    let nextState: SettingsState = {
      supported: false,
      available: false,
      requiresImage: false,
      message: "",
      groups: [],
    };
    try {
      const sessionId = await ctx.session.idOf(session);
      const result = await requireRuntimeApi(state).getRadioSettings({ sessionId });
      nextState = {
        supported: Boolean(result?.supported),
        available: Boolean(result?.available),
        requiresImage: Boolean(result?.requiresImage),
        message: String(result?.message || ""),
        groups: cloneGroups(result?.groups || []),
      };
    } catch (error) {
      log.logError(`SETTINGS LOAD FALLBACK ${errorSummary(error)}`);
      nextState.message = "Radio-wide settings could not be prepared.";
    }
    return nextState;
  }

  function applyLoadedState(nextState: SettingsState, options: { preserveCurrent?: boolean } = {}) {
    const preserveCurrent = Boolean(options.preserveCurrent);
    if (preserveCurrent && radioHasSettings() && nextState.supported) {
      const currentByKey = new Map<string, unknown>();
      for (const field of flattenSettingsFields(settingsState.groups)) {
        currentByKey.set(settingKey(field.path, field.valueIndex), field.current);
      }
      for (const field of flattenSettingsFields(nextState.groups)) {
        const key = settingKey(field.path, field.valueIndex);
        if (currentByKey.has(key)) {
          field.valueRef.current = currentByKey.get(key);
        }
      }
    }

    settingsState = nextState;
    clearInvalid();
    if (!radioHasSettings() && state.currentEditorView === "settings") {
      actions.setEditorView("channels");
    }
    ensureActiveTab();
    updateViewButtons();
    render();
  }

  // Load the selected radio's settings, applied only if its session is still
  // the current one when the runtime answers.
  async function load(options: { preserveCurrent?: boolean } = {}) {
    const session = ctx.session.current();
    const nextState = await fetchForSession(session);
    if (!ctx.session.isCurrent(session)) {
      return;
    }
    applyLoadedState(nextState, options);
  }

  // Record each issue against the field it names, so the field and its tab
  // render as invalid until the user edits it.
  function markInvalid(issue: SettingIssue): void {
    const path = Array.isArray(issue?.path) ? issue.path : [];
    const key = settingKey(path, Number(issue?.valueIndex || 0));
    invalidKeys.add(key);
    invalidMessages.set(key, String(issue?.message || "Invalid value"));
  }

  // Record per-setting issues reported by the upload preflight so the affected
  // fields and their tabs render as invalid.
  function applyValidationIssues(issues: readonly SettingIssue[] | null | undefined): void {
    (issues || []).forEach((issue) => {
      markInvalid(issue);
      const path = Array.isArray(issue?.path) ? issue.path : [];
      log.logDebug(
        `PREFLIGHT INVALID setting=${path.join(".") || "<unknown>"} value=${Number(issue?.valueIndex || 0)}: ${issue?.message || "Invalid value"}`,
      );
    });
  }

  // Mark the settings a failed export or upload says the radio refused
  // (SettingsRefusedError) and open the tab holding the first, so the field
  // the notice names is the one shown in red. The preflight cannot catch
  // every such refusal: a driver checks some values only while writing them.
  // The runtime has already logged each one to Debug Output. Any other
  // failure leaves the panel as it is.
  function markRefused(error: unknown): void {
    const issues = refusedSettingIssues(error);
    if (issues.length === 0) {
      return;
    }
    issues.forEach(markInvalid);
    const tab = String(issues[0].path[0] ?? "");
    if (findSettingsTabNode(tab)) {
      activeTab = tab;
    }
    render();
  }

  return {
    cloneGroups,
    render,
    load,
    fetchForSession,
    applyLoadedState,
    updateViewButtons,
    updateSummary,
    radioHasSettings,
    hasInvalidSettings,
    settingsUnavailableMessage,
    clearInvalid,
    ensureActiveTab,
    applyValidationIssues,
    markRefused,
    invalidCount: () => invalidKeys.size,
    getGroups: () => settingsState.groups,
    // Replace the settings tree with the one upload, export or the preflight
    // echoed back. An empty reply never replaces a tree the panel holds: the
    // runtime answers [] when it could not read settings at all (issue 97),
    // and taking that at face value would drop every setting -- and the
    // user's edits -- from the next write.
    setGroups(groups: SettingNode[] | null | undefined) {
      if (!groups || (groups.length === 0 && settingsState.groups.length > 0)) {
        return;
      }
      settingsState.groups = cloneGroups(groups);
    },
    // Wholesale replacement after a download or image load, where the settings
    // come from the image rather than a driver probe. A result with no settings
    // while the Settings view is open would leave the channels hidden behind a
    // disabled view, so it returns to Channels like applyLoadedState does.
    replaceState(nextState: SettingsState) {
      settingsState = nextState;
      activeTab = settingsState.groups[0]?.id || "";
      if (!radioHasSettings() && state.currentEditorView === "settings") {
        actions.setEditorView("channels");
      }
    },
  };
}
